<template>
  <div class="payment-page">
    <div class="page-header">
      <button class="back-btn" @click="$router.back()">‹ 返回</button>
      <div class="page-title">订单支付</div>
    </div>

    <div class="payment-section">
      <div class="section-title">订单信息</div>
      <div class="order-info">
        <div class="info-row">
          <div class="info-label">订单号</div>
          <div class="info-value">{{ orderNo }}</div>
        </div>
        <div class="info-row">
          <div class="info-label">支付金额</div>
          <div class="info-value amount">¥{{ amount }}</div>
        </div>
      </div>
    </div>

    <div v-if="confirming" class="payment-section confirming-tip">
      <div class="tip-item">正在确认支付结果，请稍候...</div>
    </div>

    <div class="payment-section">
      <div class="payment-tips" v-if="!awaitingConfirm">
        <div class="tip-item">• 点击"微信支付"后将跳转至微信完成支付</div>
        <div class="tip-item">• 支付完成后请返回本页面，点击"已完成付款"确认</div>
        <div class="tip-item">• 如遇支付问题，请联系客服</div>
      </div>
      <div class="payment-tips" v-else>
        <div class="tip-item">• 已跳转微信支付，完成后请点击下方"已完成付款"</div>
        <div class="tip-item">• 如尚未支付，可点击"重新支付"再次跳转</div>
      </div>
    </div>

    <div class="payment-actions">
      <template v-if="!awaitingConfirm">
        <button class="action-btn cancel-btn" @click="$router.back()">返回</button>
        <button class="action-btn pay-btn" :disabled="paying" @click="handlePay">
          {{ paying ? '处理中...' : '微信支付' }}
        </button>
      </template>
      <template v-else>
        <button class="action-btn cancel-btn" :disabled="confirming" @click="handlePay">重新支付</button>
        <button class="action-btn pay-btn" :disabled="confirming" @click="handleConfirmPaid">
          {{ confirming ? '确认中...' : '已完成付款' }}
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import { getOrderDetail, payOrderH5, getPayStatus } from '@/api/order'
import { getOrderStatusInfo } from '@/utils/format'

const PENDING_PAY_KEY_PREFIX = 'sweetour_pending_h5pay_'

export default {
  name: 'OrderPayment',
  data() {
    return {
      orderId: '',
      orderNo: '',
      amount: '0.00',
      order: {},
      loading: false,
      paying: false,
      confirming: false,
      awaitingConfirm: false,
      pollTimer: null
    }
  },
  async mounted() {
    this.orderId = this.$route.params.id
    if (!this.orderId) return

    await this.loadOrder()

    // 上次点了“微信支付”后离开过本页（跳去微信收银台），回来后直接进入“已完成付款”待确认态，
    // 不依赖整页刷新/浏览器前进后退等不可控的时机去自动判断，改由用户主动点击确认
    // 用 localStorage 而非 sessionStorage：微信内置浏览器跳去收银台再跳回来，部分版本会导致
    // 浏览器把这次导航当作新的标签页/上下文处理，sessionStorage 绑定的是标签页生命周期会因此丢失，
    // localStorage 绑定域名不受影响
    const pendingKey = PENDING_PAY_KEY_PREFIX + this.orderId
    if (localStorage.getItem(pendingKey)) {
      this.awaitingConfirm = true
    }
  },
  beforeDestroy() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer)
      this.pollTimer = null
    }
  },
  methods: {
    async loadOrder() {
      try {
        const res = await getOrderDetail(this.orderId)
        this.order = res.data?.order || res.data || {}
        // payAmount 可能为 0（全额优惠券抵扣），不能用 || 短路，否则会误显示原价
        const amount = this.order.payAmount ?? this.order.orderAmount ?? 0
        this.amount = Number(amount).toFixed(2)
        this.orderNo = this.order.orderNo || ''
      } catch (e) {
        console.error(e)
      }
    },
    async handlePay() {
      // 支付过程中订单状态可能已变化（如超时自动取消），点击前再校验一次而不是只依赖进入页面时的状态
      if (!getOrderStatusInfo(this.order.orderStatus).canPay) {
        this.$alert('当前订单状态不支持支付，请返回查看最新状态')
        this.goToDetail()
        return
      }

      this.paying = true
      try {
        const res = await payOrderH5(this.orderId)
        const data = res.data
        if (data && data.alreadyPaid) {
          this.goToDetail()
          return
        }
        if (data && data.h5Url) {
          // 微信 H5（MWEB）支付会离开当前页面整页跳转；跳回来后由用户手动点“已完成付款”确认。
          // localStorage 标记用于“万一没能立即跳转成功、页面被重新加载”时的兜底恢复；
          // 同时这里直接切到 awaitingConfirm=true，不等页面真正跳出去/跳回来，
          // 因为 window.location.href 赋值后跳转往往有短暂延迟，界面应立即反映“已发起支付”
          localStorage.setItem(PENDING_PAY_KEY_PREFIX + this.orderId, '1')
          this.awaitingConfirm = true
          this.paying = false
          window.location.href = data.h5Url
          return
        }
        this.$alert('获取支付参数失败，请重试')
        this.paying = false
      } catch (e) {
        console.error('发起支付失败:', e)
        this.$alert(e.message || '发起支付失败，请重试')
        this.paying = false
      }
    },
    // 用户点击“已完成付款”后主动确认一次；支付回调可能有短暂延迟，允许轮询几次
    handleConfirmPaid() {
      this.confirmPayResult(0)
    },
    confirmPayResult(attempt = 0) {
      const MAX_ATTEMPTS = 5
      this.confirming = true
      if (attempt >= MAX_ATTEMPTS) {
        this.confirming = false
        this.$alert('尚未查询到支付结果，如已支付请稍后重试确认，或联系客服')
        return
      }
      this.pollTimer = setTimeout(async () => {
        try {
          const res = await getPayStatus(this.orderId)
          if (res.data && res.data.payStatus === 1) {
            this.confirming = false
            localStorage.removeItem(PENDING_PAY_KEY_PREFIX + this.orderId)
            this.$alert('支付成功')
            this.goToOrderList()
          } else {
            this.confirmPayResult(attempt + 1)
          }
        } catch (e) {
          console.error(e)
          this.confirmPayResult(attempt + 1)
        }
      }, attempt === 0 ? 0 : 1000)
    },
    goToOrderList() {
      this.$router.replace({ name: 'OrderList' })
    },
    goToDetail() {
      this.$router.replace({ name: 'OrderDetail', params: { id: this.orderId } })
    }
  }
}
</script>

<style lang="scss" scoped>
.payment-page {
  width: 100%;
  min-height: 100vh;
  background: #f9fafb;
  padding-bottom: 80px;
}

.page-header {
  display: flex;
  align-items: center;
  background: #fff;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 20;
}

.back-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #ff720d;
  cursor: pointer;
  margin-right: 12px;
  padding: 0;
}

.page-title { font-size: 16px; font-weight: 600; color: #111827; }

.payment-section {
  background: #fff;
  margin: 10px 0;
  padding: 16px;
}

.section-title { font-size: 15px; font-weight: 600; color: #111827; margin-bottom: 12px; }

.order-info {
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px;
}

.info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  &:last-child { border-bottom: none; }
}

.info-label { font-size: 13px; color: #6b7280; }
.info-value {
  font-size: 13px;
  color: #111827;
  font-weight: 500;
  &.amount { font-size: 22px; font-weight: 700; color: #ff720d; }
}

.payment-tips {
  background: #f0f9ff;
  border-left: 3px solid #ff720d;
  border-radius: 4px;
  padding: 12px;
}
.tip-item { font-size: 13px; color: #0c4a6e; line-height: 1.9; }

.confirming-tip {
  text-align: center;
  .tip-item { color: #ff720d; font-weight: 600; }
}

.payment-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  z-index: 10;
}

.action-btn {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:not(:disabled):active { opacity: 0.85; }
}

.cancel-btn { background: #f3f4f6; color: #374151; }
.pay-btn { background: linear-gradient(90deg, #ffa43a 0%, #ff720d 100%); color: #fff; }
</style>
